import React, { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, Check, ChevronLeft, ChevronRight } from "lucide-react";
import { REFERENCE_PROJECTS, findProjectBySlug } from "../config/referenceProjects";

export default function ProjectDetail() {
  const { slug } = useParams();
  const project = findProjectBySlug(slug);
  const [lbIdx, setLbIdx] = useState(null); // lightbox index

  // Keyboard navigation for lightbox
  useEffect(() => {
    if (lbIdx === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLbIdx(null);
      if (e.key === "ArrowRight") setLbIdx((i) => (i + 1) % project.gallery.length);
      if (e.key === "ArrowLeft") setLbIdx((i) => (i - 1 + project.gallery.length) % project.gallery.length);
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = ""; window.removeEventListener("keydown", onKey); };
  }, [lbIdx, project]);

  if (!project) return <Navigate to="/" replace/>;

  const relatedProjects = REFERENCE_PROJECTS.filter((p) => p.slug !== project.slug).slice(0, 3);

  return (
    <div className="page-shell" data-testid={`project-detail-${project.slug}`}>
      {/* Breadcrumb / back */}
      <section className="site-container pt-7">
        <Link to="/" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-white/60 hover:text-[#5FA637]" data-testid="back-link">
          <ArrowLeft size={14}/> Zurück zur Startseite
        </Link>
      </section>

      {/* Hero */}
      <section className="site-container page-hero pt-8">
        <div className="eyebrow mb-6 text-[#5FA637]" data-testid="project-phase">{project.phase}</div>
        <h1 className="page-title" data-testid="project-title">
          {project.title}
        </h1>
        <p className="mt-8 max-w-3xl text-base md:text-lg text-white/75 leading-relaxed">
          {project.subtitle}
        </p>
        <p className="mt-4 text-xs text-white/40 italic">
          * Beispiel-Projekt — Visualisierung zur Veranschaulichung. Fotos freigegebener Projekte folgen.
        </p>
      </section>

      {/* Main image */}
      <section className="site-container py-8">
        <button
          onClick={() => setLbIdx(0)}
          className="block w-full aspect-[4/3] sm:aspect-[16/9] lg:aspect-[21/10] surface-panel overflow-hidden group relative"
          data-testid="project-main-image"
        >
          <img src={project.image} alt={project.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-700"/>
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A1F38]/40 to-transparent"/>
        </button>
      </section>

      {/* Content: description + meta */}
      <section className="site-container section-block">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <div className="eyebrow mb-5">Projektbeschreibung</div>
            <p className="text-base md:text-lg text-white/85 leading-relaxed mb-10" data-testid="project-description">
              {project.description}
            </p>

            {project.highlights?.length > 0 && (
              <>
                <div className="eyebrow mb-5">Highlights</div>
                <ul className="space-y-3 mb-10" data-testid="project-highlights">
                  {project.highlights.map((h, i) => (
                    <li key={i} className="flex items-start gap-3 text-white/85">
                      <Check size={18} strokeWidth={2} className="text-[#5FA637] shrink-0 mt-0.5"/>
                      <span>{h}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}

            {project.tags?.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {project.tags.map((t, i) => (
                  <span key={i} className="text-[10px] uppercase tracking-[0.12em] px-3 py-1.5 bg-white/5 border border-white/15 text-white/70">
                    {t}
                  </span>
                ))}
              </div>
            )}
          </div>

          {project.meta?.length > 0 && (
            <div className="lg:col-span-5">
              <div className="eyebrow mb-5">Projektdaten</div>
              <div className="grid grid-cols-2 gap-px bg-white/10 border border-white/10" data-testid="project-meta">
                {project.meta.map((m, i) => (
                  <div key={i} className="bg-[#112640] p-5">
                    <div className="eyebrow mb-2">{m.label}</div>
                    <div className="text-base text-white/95 font-medium">{m.value}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Gallery grid */}
      {project.gallery?.length > 1 && (
        <section className="site-container section-block border-t border-white/10">
          <div className="eyebrow mb-5 text-[#5FA637]">Weitere Bilder</div>
          <h2 className="font-display text-3xl md:text-4xl tracking-tight mb-10">Galerie.</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="project-gallery-grid">
            {project.gallery.map((src, i) => (
              <button key={i} onClick={() => setLbIdx(i)}
                className="aspect-[4/3] overflow-hidden border border-white/10 group relative"
                data-testid={`gallery-tile-${i}`}
              >
                <img src={src} alt={`${project.title} — Bild ${i + 1}`} loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"/>
                <div className="absolute inset-0 bg-[#0A1F38]/0 group-hover:bg-[#5FA637]/15 transition-colors"/>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Related projects */}
      <section className="site-container section-block border-t border-white/10">
        <div className="eyebrow mb-5 text-[#5FA637]">Weitere Projekte</div>
        <h2 className="font-display text-3xl md:text-4xl tracking-tight mb-10">Ähnliche Umsetzungen.</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="related-projects">
          {relatedProjects.map((p) => (
            <Link key={p.slug} to={`/projekte/${p.slug}`}
              data-testid={`related-${p.slug}`}
              className="technical-card block group"
            >
              <div className="aspect-[16/10] overflow-hidden">
                <img src={p.image} alt={p.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700"/>
              </div>
              <div className="p-6">
                <div className="eyebrow mb-2 text-[#5FA637]">{p.phase}</div>
                <div className="font-display text-lg mb-1 leading-tight">{p.title}</div>
                <div className="text-xs text-white/50 mt-3 inline-flex items-center gap-2 group-hover:text-[#5FA637]">
                  Projekt ansehen <ArrowRight size={12}/>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="site-container section-block">
        <div className="cta-panel text-center">
          <div className="eyebrow mb-5 text-[#5FA637]">Projekt besprechen</div>
          <h2 className="section-title max-w-3xl mx-auto">
            Ähnliches Vorhaben? <span className="italic-accent">Reden wir darüber.</span>
          </h2>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/kontakt" className="btn-primary">
              Projekt anfragen <ArrowRight size={14}/>
            </Link>
            <Link to="/produktfinder" className="btn-ghost">
              Produktfinder starten
            </Link>
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {lbIdx !== null && project.gallery?.[lbIdx] && (
        <div
          className="fixed inset-0 z-[70] bg-black/90 backdrop-blur-sm flex items-center justify-center"
          onClick={() => setLbIdx(null)}
          data-testid="project-lightbox"
        >
          <img
            src={project.gallery[lbIdx]}
            alt={`${project.title} — ${lbIdx + 1}`}
            className="max-w-[92vw] max-h-[90vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            onClick={(e) => { e.stopPropagation(); setLbIdx((i) => (i - 1 + project.gallery.length) % project.gallery.length); }}
            className="absolute left-4 md:left-10 w-12 h-12 border border-white/30 hover:border-[#5FA637] hover:text-[#5FA637] text-white flex items-center justify-center"
            aria-label="Vorheriges Bild"
            data-testid="lightbox-prev"
          >
            <ChevronLeft size={18}/>
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); setLbIdx((i) => (i + 1) % project.gallery.length); }}
            className="absolute right-4 md:right-10 w-12 h-12 border border-white/30 hover:border-[#5FA637] hover:text-[#5FA637] text-white flex items-center justify-center"
            aria-label="Nächstes Bild"
            data-testid="lightbox-next"
          >
            <ChevronRight size={18}/>
          </button>
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/70 text-sm">
            {lbIdx + 1} / {project.gallery.length}
          </div>
        </div>
      )}
    </div>
  );
}
